import { useEffect, useMemo, useRef, useState } from "react";
import { Bot, Loader2, Mic, MicOff, Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

export type AssistantMessage = {
  id: string;
  role: "assistant" | "user";
  content: string;
  variant?: "chat" | "command";
  timestamp: number;
};

interface AIAssistPanelProps {
  messages: AssistantMessage[];
  busy: boolean;
  onSendChat: (text: string) => void;
  onRunCommand: (text: string) => void;
  onQuickCommand?: (prompt: string) => void;
  onSaveToScratchPad?: (content: string, title?: string) => void;
}

const quickCommands = [
  { label: "Open Scratch", prompt: "Open my scratch pad and read the latest note" },
  { label: "Review Thesis", prompt: "Open the thesis panel and review my current thesis" },
  { label: "Summarize Draft", prompt: "Summarize this document so far" },
  { label: "Show Citations", prompt: "Open my references and tell me if anything is missing" },
];

// Convert markdown links to HTML
function formatMessageContent(content: string): string {
  // Convert markdown links [text](url) to HTML
  return content
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>') // Bold
    .replace(/\n/g, '<br/>'); // Line breaks
}

export default function AIAssistPanel({
  messages,
  busy,
  onSendChat,
  onRunCommand,
  onQuickCommand,
  onSaveToScratchPad,
}: AIAssistPanelProps) {
  const [inputValue, setInputValue] = useState("");
  const [mode, setMode] = useState<"chat" | "command">("command");
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [voiceActive, setVoiceActive] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; messageId: string } | null>(null);
  const recognitionRef = useRef<any>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event: any) => {
      const text = event.results?.[0]?.[0]?.transcript;
      if (text) {
        setInputValue(text);
        setVoiceActive(false);
        handleSubmit(text, mode === "command");
      }
    };
    recognition.onerror = () => setVoiceActive(false);
    recognition.onend = () => setVoiceActive(false);
    recognitionRef.current = recognition;
    setVoiceSupported(true);
    return () => {
      recognition.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const lastAssistantReply = useMemo(() => {
    const reversed = [...messages].reverse();
    return reversed.find((msg) => msg.role === "assistant")?.content;
  }, [messages]);

  const handleSubmit = (text?: string, asCommand = mode === "command") => {
    const trimmed = (text ?? inputValue).trim();
    if (!trimmed) return;
    if (asCommand) {
      onRunCommand(trimmed);
    } else {
      onSendChat(trimmed);
    }
    setInputValue("");
  };

  const toggleVoice = () => {
    if (!voiceSupported || !recognitionRef.current) return;
    if (voiceActive) {
      recognitionRef.current.stop();
      setVoiceActive(false);
      return;
    }
    try {
      setVoiceActive(true);
      recognitionRef.current.start();
    } catch (error) {
      console.warn("voice start failed", error);
      setVoiceActive(false);
    }
  };

  const handleRightClick = (e: React.MouseEvent, messageId: string) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, messageId });
  };

  const handleSaveMessage = (messageId: string) => {
    const message = messages.find(m => m.id === messageId);
    if (message && onSaveToScratchPad) {
      const title = message.role === 'user' 
        ? 'My Query' 
        : 'AI Response';
      onSaveToScratchPad(message.content, title);
    }
    setContextMenu(null);
  };

  return (
    <div className="flex flex-col h-full bg-slate-900/40 rounded-b-xl border border-white/10">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2 text-white">
          <Bot className="w-5 h-5 text-purple-300" />
          <div>
            <p className="text-sm font-semibold">AI Assist</p>
            <p className="text-[11px] text-white/60">
              {busy ? "Thinking..." : lastAssistantReply ? "Ready for your next request" : "Ask for help or give a command"}
            </p>
          </div>
        </div>
        <div className="inline-flex rounded-full bg-white/10 text-[11px] text-white/80">
          <button
            type="button"
            onClick={() => setMode("command")}
            className={`px-3 py-1 rounded-full transition ${mode === "command" ? "bg-white/20 text-white" : "text-white/60"}`}
          >
            Command
          </button>
          <button
            type="button"
            onClick={() => setMode("chat")}
            className={`px-3 py-1 rounded-full transition ${mode === "chat" ? "bg-white/20 text-white" : "text-white/60"}`}
          >
            Chat
          </button>
        </div>
      </div>

      {/* Quick actions or back button */}
      {messages.length > 0 ? (
        <div className="px-4 py-3 border-b border-white/5">
          <button
            onClick={() => {
              setInputValue('');
              // Don't clear messages, just show quick actions again
            }}
            className="flex items-center gap-2 text-[11px] text-white/70 hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Quick Actions
          </button>
        </div>
      ) : (
        <div className="px-4 py-3 border-b border-white/5">
          <p className="text-[11px] text-white/70 mb-2 uppercase tracking-wide">Quick actions</p>
          <div className="flex flex-wrap gap-2">
            {quickCommands.map((cmd) => (
              <button
                key={cmd.label}
                type="button"
                onClick={() => {
                  if (onQuickCommand) {
                    onQuickCommand(cmd.prompt);
                  } else {
                    handleSubmit(cmd.prompt, true);
                  }
                }}
                className="text-[11px] px-3 py-1.5 rounded-full border border-white/15 text-white/80 hover:bg-white/10"
              >
                {cmd.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-white/50 text-sm py-8">
            <p className="mb-2">Ask the assistant to find research, explain concepts, or get help with WHIPT features.</p>
            <p className="text-xs text-white/30">💡 Tip: Right-click any message to save it to your Scratch Pad</p>
          </div>
        )}
        {messages.map((message) => (
          <div 
            key={message.id} 
            className={`flex ${message.role === "assistant" ? "justify-start" : "justify-end"} group`}
            onContextMenu={(e) => handleRightClick(e, message.id)}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow relative ${
                message.role === "assistant"
                  ? "bg-purple-500/80 text-white hover:bg-purple-500/90"
                  : "bg-slate-800 text-white hover:bg-slate-700"
              } transition-colors cursor-pointer break-words overflow-wrap-anywhere`}
            >
              {/* Save hint on hover */}
              <div className="absolute -top-6 left-0 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] text-white/50 whitespace-nowrap">
                Right-click to save →
              </div>
              
              {message.variant === "command" && (
                <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide text-white/60">
                  <Sparkles className="w-3 h-3" /> Command
                </span>
              )}
              <div 
                className="whitespace-pre-wrap leading-relaxed prose prose-invert prose-sm max-w-none prose-a:text-cyan-300 prose-a:underline hover:prose-a:text-cyan-200 break-words"
                dangerouslySetInnerHTML={{ __html: formatMessageContent(message.content) }}
              />
            </div>
          </div>
        ))}
        {messages.length > 0 && !busy && (
          <div className="text-center text-[10px] text-white/30 py-2">
            💡 Right-click any message to save it
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-slate-800 border border-slate-600 rounded-lg shadow-xl py-1 z-[10000]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => handleSaveMessage(contextMenu.messageId)}
            className="w-full px-4 py-2 text-left text-sm text-white hover:bg-slate-700 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            Save to Scratch Pad
          </button>
        </div>
      )}

      <form
        className="px-4 py-3 border-t border-white/10 flex items-start gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <div className="flex-1">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder={mode === "command" ? "Ask me to open or do something..." : "Chat with your writing partner"}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-white/30 resize-none min-h-[40px] max-h-[120px] overflow-y-auto"
            rows={1}
            style={{ height: 'auto' }}
            ref={(el) => {
              if (el) {
                el.style.height = 'auto';
                el.style.height = el.scrollHeight + 'px';
              }
            }}
          />
        </div>
        {voiceSupported && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={toggleVoice}
            className={`w-9 h-9 border border-white/10 ${voiceActive ? "bg-purple-500/30" : "bg-white/5"}`}
          >
            {voiceActive ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </Button>
        )}
        <Button type="submit" disabled={busy || !inputValue.trim()} className="gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {mode === "command" ? "Run" : "Send"}
        </Button>
      </form>
    </div>
  );
}
